import { useState, useEffect, useCallback, useRef } from 'react';
export const useChatHistory = (token, apiClient) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isLoadingRef = useRef(false); // Prevent double-loading from StrictMode

  // Use the passed apiClient directly

  // Load chats from backend
  const loadChats = useCallback(async (forceRefresh = false) => {
    if (!token) return;

    // Prevent double-loading from React StrictMode
    if (isLoadingRef.current) {
      console.log('Already loading chats, skipping duplicate call');
      return;
    }
    isLoadingRef.current = true;

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/chat/chats');
      const chatsData = response.data.chats || [];

      // Transform backend format to frontend format
      const transformedChats = chatsData.map(chat => ({
        id: chat.id,
        title: chat.title,
        conversation: chat.messages.map(msg => ({
          id: msg.id, // Preserve message ID for deduplication
          type: msg.type,
          message: msg.message,
          timestamp: msg.timestamp || null,
        })),
        createdAt: new Date(chat.created_at),
        updatedAt: new Date(chat.updated_at),
        isActive: false,
      }));

      setChats(transformedChats);
      return transformedChats;
    } catch (err) {
      console.error('Failed to load chats:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load chats');
      return [];
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [token, apiClient]);

  // Utility function to generate a brief title from a message
  const generateTitleFromMessage = (message) => {
    if (!message || typeof message !== 'string') return 'New Chat';

    // Clean the message: remove markdown, extra whitespace, special chars
    let cleaned = message
      .replace(/[#*_`~[\]()]/g, '') // Remove markdown formatting
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();

    // Remove common question prefixes to get to the meat of the question
    cleaned = cleaned
      .replace(/^(show me|tell me about|what is|what are|how|why|when|where|can you|please)\s+/i, '')
      .trim();

    // Capitalize first letter
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);

    // Truncate to reasonable length (50 chars) and add ellipsis if needed
    if (cleaned.length > 50) {
      cleaned = cleaned.substring(0, 50).trim() + '...';
    }

    return cleaned || 'New Chat';
  };

  // Create new chat
  const createChat = useCallback(async (title = 'New Chat') => {
    if (!token) return null;

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/chat/chats', { title });
      const chat = response.data;

      const newChat = {
        id: chat.id,
        title: chat.title,
        conversation: [],
        createdAt: new Date(chat.created_at),
        updatedAt: new Date(chat.updated_at),
        isActive: false,
      };

      setChats(prev => [newChat, ...prev]);
      return newChat;
    } catch (err) {
      console.error('Failed to create chat:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create chat');
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, apiClient]);

  // Add message to chat
  // Now accepts messageId from frontend to prevent duplicate IDs
  const addMessage = useCallback(async (chatId, type, message, messageId = null) => {
    if (!token) return false;

    try {
      await apiClient.post(`/chat/chats/${chatId}/messages`, {
        type,
        message,
        id: messageId, // Send frontend's message ID to backend
      });
      return true;
    } catch (err) {
      console.error('Failed to add message:', err);
      return false;
    }
  }, [token, apiClient]);

  // Update chat title
  const updateChatTitle = useCallback(async (chatId, title) => {
    if (!token) return false;

    try {
      await apiClient.put(`/chat/chats/${chatId}`, null, {
        params: { title },
      });
      setChats(prev => prev.map(chat =>
        chat.id === chatId ? { ...chat, title } : chat
      ));
      return true;
    } catch (err) {
      console.error('Failed to update chat title:', err);
      return false;
    }
  }, [token, apiClient]);

  // Delete chat
  const deleteChat = useCallback(async (chatId) => {
    if (!token) return false;

    try {
      await apiClient.delete(`/chat/chats/${chatId}`);
      setChats(prev => prev.filter(chat => chat.id !== chatId));
      return true;
    } catch (err) {
      console.error('Failed to delete chat:', err);
      return false;
    }
  }, [token, apiClient]);

  // Clear chat messages
  const clearChat = useCallback(async (chatId) => {
    if (!token) return false;

    try {
      await apiClient.post(`/chat/chats/${chatId}/clear`);
      setChats(prev => prev.map(chat =>
        chat.id === chatId ? { ...chat, conversation: [] } : chat
      ));
      return true;
    } catch (err) {
      console.error('Failed to clear chat:', err);
      return false;
    }
  }, [token, apiClient]);

  // Load chats on mount if token exists
  useEffect(() => {
    if (token) {
      loadChats();
    }
  }, [token, loadChats]);

  // Auto-update chat title from first user message
  const updateChatTitleFromFirstMessage = useCallback(async (chatId, firstUserMessage) => {
    if (!token || !firstUserMessage) return false;

    const title = generateTitleFromMessage(firstUserMessage);

    // Only update if title is different from 'New Chat'
    if (title && title !== 'New Chat') {
      return await updateChatTitle(chatId, title);
    }
    return false;
  }, [token, updateChatTitle]);

  return {
    chats,
    loading,
    error,
    loadChats,
    createChat,
    addMessage,
    updateChatTitle,
    updateChatTitleFromFirstMessage,
    deleteChat,
    clearChat,
    generateTitleFromMessage, // Export for external use
  };
};

